import { useCallback, useEffect, useMemo, useState } from 'react';

import User from 'src/models/User';
import ProductService from 'src/services/product-service';
import {
  trackPostPurchaseUpsellViewed,
  trackPostPurchaseAddonSelected,
  trackPostPurchaseUpsellDeclined,
} from 'src/util/track';
import { shouldShowChallengeNutritionUpsell } from '../utils/signupPurchase.util';

type UseChallengeNutritionUpsellParams = {
  isChallengePackageFlow: boolean;
  isAustraliaUser: boolean;
  nutritionCoachingPrice: number;
  nutritionCoachingPriceId: string;
  isLegacyChallengeCoachSelected: boolean;
  user?: User;
  onClearLegacyChallengeCoachSelection: () => void;
  onComplete: (additionalPriceIds?: string[]) => void;
  selectedPlanId?: string;
};

type OpenChallengeNutritionUpsellParams = {
  customerId?: string;
  paymentMethodId: string;
};

export const useChallengeNutritionUpsell = ({
  isChallengePackageFlow,
  isAustraliaUser,
  nutritionCoachingPrice,
  nutritionCoachingPriceId,
  isLegacyChallengeCoachSelected,
  user,
  onClearLegacyChallengeCoachSelection,
  onComplete,
  selectedPlanId,
}: UseChallengeNutritionUpsellParams) => {
  const [isVisible, setIsVisible] = useState(false);
  const [customerId, setCustomerId] = useState<string>();
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isEligible = useMemo(
    () =>
      shouldShowChallengeNutritionUpsell({
        isChallengePackageFlow,
        isAustraliaUser,
        nutritionCoachingPriceId,
        nutritionCoachingPrice,
      }),
    [
      isChallengePackageFlow,
      isAustraliaUser,
      nutritionCoachingPriceId,
      nutritionCoachingPrice,
    ]
  );

  useEffect(() => {
    if (isEligible && isLegacyChallengeCoachSelected) {
      onClearLegacyChallengeCoachSelection();
    }
  }, [
    isEligible,
    isLegacyChallengeCoachSelected,
    onClearLegacyChallengeCoachSelection,
  ]);

  const completeFlow = useCallback(
    (additionalPriceIds: string[] = []) => {
      setIsVisible(false);
      setCustomerId(undefined);
      setPaymentMethodId(undefined);
      setErrorMessage('');
      setIsSubmitting(false);
      onComplete(additionalPriceIds);
    },
    [onComplete]
  );

  const openUpsell = useCallback(
    ({ customerId, paymentMethodId }: OpenChallengeNutritionUpsellParams) => {
      if (!isEligible) {
        return false;
      }

      trackPostPurchaseUpsellViewed('nutrition_coaching', selectedPlanId);
      setCustomerId(customerId);
      setPaymentMethodId(paymentMethodId);
      setErrorMessage('');
      setIsVisible(true);
      return true;
    },
    [isEligible, selectedPlanId]
  );

  const handleDecline = useCallback(() => {
    if (!isSubmitting) {
      trackPostPurchaseUpsellDeclined('nutrition_coaching', selectedPlanId);
      completeFlow();
    }
  }, [completeFlow, isSubmitting, selectedPlanId]);

  const handleConfirm = useCallback(async () => {
    if (!user || !paymentMethodId || !nutritionCoachingPriceId) {
      setErrorMessage(
        'Unable to confirm nutrition coaching right now. Please try again.'
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      await ProductService.purchaseNutritionCoachingAddon({
        userId: user.id,
        email: user.email,
        paymentMethodId,
        priceIds: [nutritionCoachingPriceId],
        customerId,
        isAfterPay: false,
        isCardPayment: true,
      });

      trackPostPurchaseAddonSelected('nutrition_coaching', selectedPlanId);
      completeFlow([nutritionCoachingPriceId]);
    } catch (error) {
      console.error('Challenge nutrition add-on purchase failed:', error);
      setErrorMessage(
        'Failed to purchase nutrition coaching. Please try again.'
      );
      setIsSubmitting(false);
    }
  }, [
    user,
    paymentMethodId,
    nutritionCoachingPriceId,
    customerId,
    completeFlow,
    selectedPlanId,
  ]);

  return {
    isEligible,
    isVisible,
    isSubmitting,
    errorMessage,
    openUpsell,
    handleDecline,
    handleConfirm,
  };
};
